
import { Component, Vue, Prop } from "vue-property-decorator";
import { plainToInstance } from "class-transformer";

import subscription_routes from "@/api/routes/subscriptions";

import { getIconFromUnitableType } from "@/helpers";

import Preloader from "@/components/Preloader.vue";
import CComment from "@/components/Comment.vue";
import CFileDownload from "@/components/FileDownload.vue";

import { ESkillLevel } from "@/enums/skill_level";
import SubscriptionProgram from "@/models/subscriptions/program";
import { ESubscriptionStatus } from "@/models/subscriptions/status";
import SubscriptionUnit from "@/models/subscriptions/unit";

@Component({
  name: "VSubscriptionProgram",
  components: {
    Preloader,
    CComment,
    CFileDownload
  }
})
export default class VSubscriptionProgram extends Vue {
  @Prop({ required: true }) private id!: string;

  public preload: boolean = false;

  public getIconFromUnitableType = getIconFromUnitableType;

  public is_current_user_subscription: boolean = false;
  public skill_has_max_level: boolean = false;
  public subscription_program: SubscriptionProgram | null = null;
  public subscription_units: Map<number, SubscriptionUnit[]> = new Map();

  public show_levels: Set<number> = new Set();
  public show_levels_key: number = 0;

  public comment_text: string = "";
  public comment_files: string[] = [];

  private current_user: IJWTUser | null = this.$store.state.currentUser;
  private watchers: Function[] = [];

  protected beforeDestroy() {
    this.watchers.forEach(unwatch => {
      unwatch();
    });
  }

  protected async created() {
    this.watchers.push(
      this.$store.watch(
        state => state.currentUser,
        currentUser => {
          this.current_user = currentUser;

          this.setIsCurrentUserSubscription();
        }
      )
    );

    await this.loadSubscriptionProgram();
  }

  public uploadFile(file_url: string) {
    if (this.comment_files.length < 10) {
      this.comment_files.push(file_url);
    } else {
      this.$notify({
        group: "notifications",
        type: "error",
        text: "Не более 10 файлов",
        duration: 500,
        speed: 500
      });
    }
  }

  public removeFile(index: number) {
    this.comment_files.splice(index, 1);
  }

  public toggleLevel(level: number) {
    if (this.show_levels.has(level)) {
      this.show_levels.delete(level);
    } else {
      this.show_levels.add(level);
    }

    this.show_levels_key++;
  }

  public showLevel(level: number) {
    return this.show_levels.has(level);
  }

  public statusTagColor() {
    if (this.subscription_program) {
      switch (this.subscription_program.status) {
        case ESubscriptionStatus.DONE:
          return "tag--green";
        case ESubscriptionStatus.NOT_STARTED:
        case ESubscriptionStatus.IN_PROGRESS:
          return "tag--blue";
        case ESubscriptionStatus.FAILED:
          return "tag--red";
        default:
          return "tag--orange";
      }
    }

    return "tag--orange";
  }

  public relationLink(
    relation_level: number,
    subscription_unit: SubscriptionUnit
  ) {
    if (this.subscription_program) {
      const unit_is_done =
        subscription_unit.status === ESubscriptionStatus.DONE &&
        subscription_unit.subscription_unit_is_started;

      const unit_is_available =
        (relation_level <= this.subscription_program.current_skill_level + 1 ||
          this.subscription_program.program_assignment_id != null) &&
        ![
          ESubscriptionStatus.AWAITING_CONFIRMATION,
          ESubscriptionStatus.AWAITING_CONTINUE,
          ESubscriptionStatus.AWAITING_COMPLETION
        ].includes(this.subscription_program.status);

      if (
        (this.is_current_user_subscription && unit_is_available) ||
        unit_is_done
      ) {
        return {
          name: "subscription_unit",
          params: {
            id: this.subscription_program.id,
            unit_id: subscription_unit.id
          }
        };
      }
    }

    return "";
  }

  public relationStatusIcon(status_id: ESubscriptionStatus) {
    switch (status_id) {
      case ESubscriptionStatus.NOT_STARTED:
        return null;
      case ESubscriptionStatus.IN_PROGRESS:
        return "icon-clock-gray.svg";
      case ESubscriptionStatus.DONE:
        return "icon-done-green.svg";
      case ESubscriptionStatus.FAILED:
        return "icon-close-red.svg";
      case ESubscriptionStatus.AWAITING_CONFIRMATION:
        return "icon-reschedule.svg";
      case ESubscriptionStatus.AWAITING_REPEAT_PASS:
        return "icon-reschedule.svg";
      case ESubscriptionStatus.AWAITING_CONTINUE:
        return "icon-reschedule.svg";
      case ESubscriptionStatus.AWAITING_COMPLETION:
        return "icon-reschedule.svg";
      default:
        return null;
    }
  }

  private setIsCurrentUserSubscription() {
    if (this.subscription_program && this.current_user) {
      this.is_current_user_subscription =
        this.subscription_program.user_id === this.current_user.id;
    } else {
      this.is_current_user_subscription = false;
    }
  }

  public async subscriptionContinue() {
    if (!this.subscription_program || !this.subscription_program.awaitingNext) {
      return;
    }

    this.preload = true;

    return this.$api
      .put(subscription_routes.continue(this.id), {
        skill_id: this.subscription_program.skill_id,
        comment_files: this.comment_files,
        comment_text: this.comment_text
      })
      .then(({ data: res }: { data: SubscriptionProgram }) => {
        this.subscription_program = plainToInstance(SubscriptionProgram, res);

        this.setIsCurrentUserSubscription();

        this.skill_has_max_level =
          Number(this.subscription_program.current_skill_level) ===
          ESkillLevel.MAX_LEVEL;

        if (
          this.subscription_program.current_skill_level < ESkillLevel.MAX_LEVEL
        ) {
          this.show_levels.add(
            this.subscription_program.current_skill_level + 1
          );
          this.show_levels_key++;
        }

        this.subscription_units.clear();

        this.subscription_program.subscription_units.forEach(su => {
          const level = su.relation_version_level;

          if (this.subscription_units.has(level)) {
            this.subscription_units.get(level)!.push(su);
          } else {
            this.subscription_units.set(level, [su]);
          }
        });
      })
      .catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          speed: 500
        });
      })
      .finally(() => {
        this.preload = false;
      });
  }

  private async loadSubscriptionProgram() {
    this.preload = true;

    return this.$api
      .get(subscription_routes.subscription_program(this.id), {
        params: {
          units: true,
          inspectors: true
        }
      })
      .then(({ data: res }: { data: SubscriptionProgram }) => {
        this.subscription_program = plainToInstance(SubscriptionProgram, res);

        this.setIsCurrentUserSubscription();

        this.skill_has_max_level =
          Number(this.subscription_program.current_skill_level) ===
          ESkillLevel.MAX_LEVEL;

        if (
          this.subscription_program.current_skill_level < ESkillLevel.MAX_LEVEL
        ) {
          this.show_levels.add(
            this.subscription_program.current_skill_level + 1
          );
          this.show_levels_key++;
        }

        this.subscription_program.subscription_units.forEach(su => {
          const level = su.relation_version_level;

          if (this.subscription_units.has(level)) {
            this.subscription_units.get(level)!.push(su);
          } else {
            this.subscription_units.set(level, [su]);
          }
        });
      })
      .catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          speed: 500
        });
      })
      .finally(() => {
        this.preload = false;
      });
  }
}
